import { type ReactElement } from 'react';
import { cn } from '@/lib/cn';
import type { FirstPracticeChoice } from '@/lib/first-practice';

type CardOption = {
  choice: FirstPracticeChoice;
  label: string;
};

const CARD_OPTIONS: CardOption[] = [
  { choice: 'today', label: 'Today' },
  { choice: 'tomorrow', label: 'Tomorrow' },
  { choice: 'weekend', label: 'This weekend' },
  { choice: 'custom', label: 'Pick a date' },
];

type Props = {
  choice: FirstPracticeChoice | null;
  onChange: (choice: FirstPracticeChoice, customDate?: string) => void;
  customDate?: string;
};

function handleKeyActivation(
  event: React.KeyboardEvent,
  choice: FirstPracticeChoice,
  onChange: Props['onChange'],
  customDate?: string,
): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onChange(choice, customDate);
  }
}

export function FirstPracticeChoiceCard({ choice, onChange, customDate }: Props): ReactElement {
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {CARD_OPTIONS.map((option) => {
          const isSelected = choice === option.choice;
          return (
            <div
              key={option.choice}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => onChange(option.choice, customDate)}
              onKeyDown={(e) => handleKeyActivation(e, option.choice, onChange, customDate)}
              className={cn(
                'flex items-center justify-center rounded-[var(--r-sm)] border p-4 cursor-pointer transition-all text-sm font-semibold',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-surface)]',
                isSelected
                  ? 'border-[var(--brand)] bg-[var(--brand)]/5 text-[var(--ink)]'
                  : 'border-[var(--rule)] bg-[var(--bg-surface)] text-[var(--ink)] hover:border-[var(--brand)]',
              )}
            >
              {option.label}
            </div>
          );
        })}
      </div>

      {choice === 'custom' && (
        <input
          type="date"
          value={customDate ?? ''}
          onChange={(event) => onChange('custom', event.target.value)}
          className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20"
        />
      )}
    </div>
  );
}
